import React, { useEffect } from "react";
import ItemOptions from "./item-options";
import { get } from "lodash";
import { getHexCodeColor } from "~/utils/converters";

type Props = {
  data: Array<any>;
  onChange?: (dataChange: any) => void;
  onValidate?: (isValid: boolean) => void;
  error?: any;
  slug?: string;
  dataNote?: any;
  value?: {
    name: string;
    type: any;
    value: string;
  }[];
};

const CustomizationOptions = ({
  onValidate,
  onChange,
  error,
  value,
  slug,
  dataNote,
}: Props) => {
  const [dataChange, setDataChange] = React.useState<{
    options: string[];
    optionById: {
      [key: string]: {
        name: string;
        type: any;
        value: string;
        required: boolean;
        active: boolean;
        radio: boolean;
        radioValue: boolean;
        [key: string]: any;
      };
    };
  }>({
    options: [],
    optionById: {},
  });
  const firstRender = React.useRef(true);
  const handleOnchange = (data: { name: string; value: string }) => {
    if (data.name === "color") return;
    // const index = dataChange?.options?.findIndex((item: any) => item.name === data.name);
    const tempOptionById = dataChange.optionById;
    if (!!tempOptionById[data.name]) {
      tempOptionById[data.name].value = data.value;
      setDataChange((prev: any) => ({
        ...prev,
        optionById: {
          ...prev.optionById,
          ...tempOptionById,
        },
      }));
    }
    firstRender.current = false;
  };

  const handleRadioChange = (name: string, radioValue: boolean) => {
    setDataChange((prev: any) => ({
      ...prev,
      optionById: {
        ...prev.optionById,
        [name]: {
          ...prev.optionById[name],
          radioValue: radioValue,
          // Clear the value if radio is set to No
          value: radioValue ? prev.optionById[name]?.value || "" : "",
        },
      },
    }));
  };

  const data: any[] = get(dataNote, "products.items[0].product_note", []);
  useEffect(() => {
    if (data?.length > 0 && dataChange?.options?.length > 0) {
      const tempOptionById = dataChange.optionById;
      const isValid = dataChange?.options
        ?.filter((i: any) => {
          return !!tempOptionById[i]?.required;
        })
        ?.every((item: any) => {
          const option = tempOptionById[item];
          if (!option) return false;

          // If it's a radio item and radioValue is false (No), it's valid even without a value
          if (option.radio && option.radioValue === false) {
            return true;
          }

          // For radio items with radioValue true (Yes) or non-radio items, check if value exists
          return !!option.value;
        });

      onValidate && onValidate(isValid);
    }
  }, [dataChange?.optionById]);

  useEffect(() => {
    if (data?.length > 0) {
      const tempOptionById: any = {};
      const tempOptions: any = [];
      data.forEach((item: any) => {
        const _value = value?.find((i) => i.name === item.name);
        tempOptionById[item.name] = {
          ...item,
          value:
            item.name !== "color"
              ? _value?.value
              : _value?.value
                ? getHexCodeColor(_value?.value)
                : "",
          radioValue: item.radio ? true : undefined, // Default to true for radio items
        };
        tempOptions.push(item.name);
      });
      const newData: any = {
        options: tempOptions,
        optionById: tempOptionById,
      };
      setDataChange(newData);
    }
  }, [data, value]);
  useEffect(() => {
    if (
      dataChange?.optionById?.color &&
      !dataChange?.optionById?.color?.value
    ) {
      const cur = {
        ...dataChange,
        optionById: {
          ...dataChange.optionById,
          color: {
            ...dataChange.optionById.color,
            value: getHexCodeColor(
              dataChange?.optionById?.color?.options?.[0]?.label
            ),
          },
        },
      };
      onChange && onChange(cur);
    } else {
      onChange && onChange(dataChange);
    }
  }, [dataChange]);

  return (
    <div className="mt-2">
      {data
        ?.filter((item) => item.type !== "color")
        ?.map((item, index: number) => {
          const currentOption = dataChange.optionById[item.name];
          const showItemOptions = !item.radio || currentOption?.radioValue;

          return (
            <div key={index}>
              <div className="flex">
                <label className="mb-2 capitalize">
                  {item.label}{" "}
                  {item.required ? (
                    <span className="text-red-500"> (*) </span>
                  ) : (
                    ""
                  )}
                  :
                </label>
              </div>

              {item.radio && (
                <div className="mb-3">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`radio-${item.name}`}
                        value="yes"
                        checked={currentOption?.radioValue === true}
                        onChange={() => handleRadioChange(item.name, true)}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`radio-${item.name}`}
                        value="no"
                        checked={currentOption?.radioValue === false}
                        onChange={() => handleRadioChange(item.name, false)}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>
              )}

              {showItemOptions && (
                <ItemOptions
                  data={item}
                  onChange={handleOnchange}
                  value={dataChange.optionById[item.name]?.value}
                  error={error?.[item?.name]}
                />
              )}
            </div>
          );
        })}
    </div>
  );
};

export default CustomizationOptions;
